import Alpine from "alpinejs"
import { load, store } from "../localStorage"
import validate from "../validations"
import { convertArabicDigits } from "../helpers"
import { getFrontendCustomField } from "../frontendCustomFields"

const VIEW_STORAGE_KEY = "view"
const GLOBAL_STORAGE_KEY = "data"

const commonView = ({
  nextStepPath,
  formFields,
  redirectIfWeightGainUrl,
  isSkipStep
} = {}) => ({
  statusToTrack: null,
  isSubmitAllowed: false,
  showSpinner: false,
  formValues: {},
  errors: {},
  showFetchError: false,
  nextStepPath: null,

  initFormValues(formFields) {
    if (!formFields) return

    const viewData = load(VIEW_STORAGE_KEY)
    const globalData = load(GLOBAL_STORAGE_KEY)

    const isActiveErrors = viewData?.errors && viewData.path === window.location.pathname

    if (isActiveErrors) {
      this.formValues = viewData.formValues
      this.errors = viewData.errors
    } else {
      formFields.map((field) => {
        const value = globalData?.values[field]
        
        this.formValues[field] = value
      })
    }

    this.setSubmitAllowed(this.formValues, formFields)
  },

  commonInit() {
    this.store = Alpine.store("data")
    this.pageState = Alpine.store("pageState")
    this.nextStepPath = nextStepPath || this.pageState?.nextStepPath

    window.addEventListener("pageshow", (event) => {
      if (event.persisted) {
        // When the page is restored from back/forward cache the Alpine store is frozen at the
        // state it had when the page was navigated away from. Any values written to localStorage
        // by later pages (e.g. chat answers, subsequent form steps) are invisible to this frozen
        // store. Sync from localStorage so that submit() spreads the up-to-date values.
        const latestData = load(GLOBAL_STORAGE_KEY)
        if (latestData?.values) {
          this.store.values = { ...this.store.values, ...latestData.values }
        }

        this.initFormValues(formFields)

        this.showSpinner = false
        this.isSubmitAllowed = true
      }

      this.store.trackStatusForAnalytics(this.statusToTrack)
    })

    this.initFormValues(formFields)

    this.redirectToNext(isSkipStep)

    // Edge case gandling
    if (redirectIfWeightGainUrl) {
      this.redirectIfWeightGain(redirectIfWeightGainUrl)
    }
  },

  init() {
    this.commonInit()
  },

  isSelected(name, value) {
    return Boolean(this.formValues[name]?.includes(value))
  },

  isMetric() {
    return this.store.values.measurementSystem === "metric"
  },

  isImperial() {
    return this.store.values.measurementSystem === "imperial"
  },

  setMeasurementSystem(value) {
    if (!value) return

    this.store.values.measurementSystem = value
  },

  resetTrackerUserData() {
    return new Promise((resolve) => {
      this.store.internalTracker.resetUserData("klaviyo")
      // Resolve after the synchronous operation completes to imitate async behavior
      setTimeout(resolve, 0)
    })
  },

  selectSingleValue(field, value) {
    this.formValues[field] = value
    this.setSubmitAllowed(this.formValues, formFields)
  },

  update(field, value) {
    this.formValues[field] = value
    this.submit()
  },

  updateAndRedirect(field, value, redirectPath) {
    this.formValues[field] = value
    this.submit({ redirectPath })
  },

  handleInput($event, field) {
    this.formValues[field] = $event.target.value && $event.target.value.trim()

    this.errors[field] = null

    this.setSubmitAllowed(this.formValues, formFields)
  },

  handleNumericInput($event, field) {
    $event.target.value = $event.target.value.replace(/[^\d\u0660-\u0669]/g, "")

    $event.target.value = convertArabicDigits($event.target.value)

    this.formValues[field] = $event.target.value && Number($event.target.value)

    this.errors[field] = null

    this.setSubmitAllowed(this.formValues, formFields)
  },

  getRequiredFields(formFields) {
    // Treat all formFields as required by default
    if (Array.isArray(formFields)) {
      return formFields
    }

    // Provide the ability to pass the extended config for the formFields
    return Object.keys(formFields).map((key) => {
      if (formFields[key].required) {
        return key
      }
    })
  },

  setSubmitAllowed(formValues, formFields) {
    this.validateRequired(formValues, formFields)
  },

  validateRequired(formValues, formFields) {
    const requiredFields = this.getRequiredFields(formFields)
    const allRequiredPresent = requiredFields.every((value) => {
      const formValue = formValues[value]

      if (Array.isArray(formValue)) return !!formValue.length

      return formValue && formValue !== 0
    })

    this.isSubmitAllowed = allRequiredPresent
  },

  resetErrors() {
    this.errors = {}
  },

  processFormValues() {
    return this.formValues
  },

  storeViewData(data) {
    store(VIEW_STORAGE_KEY, {
      path: window.location.pathname,
      ...data,
    })
  },

  async submit({ push, redirectPath, isTrackingResetNeeded } = {}) {
    const nextPath = redirectPath || this.nextStepPath
    const formValues = this.processFormValues()

    const { hasErrors, errors } = validate(formValues, this.store)

    if (hasErrors) {
      this.storeViewData({ errors, formValues: this.formValues })

      this.errors = { ...this.errors, ...errors }
    } else {
      this.showSpinner = true
      this.isSubmitAllowed = false

      this.storeViewData(null)
      this.store.values = { ...this.store.values, ...formValues }
      // Alpine.effect uses queueMicrotask — write synchronously so localStorage
      // is always up-to-date before navigation starts, regardless of browser behavior.
      this.store.writeToLocalStorage(this.store.values)

      if (!push) {
        if (isTrackingResetNeeded) await this.resetTrackerUserData()

        window.location = nextPath
      } else if (await this.store.push()) {
        if (isTrackingResetNeeded) await this.resetTrackerUserData()

        window.location = nextPath
      } else {
        if (this.store.emailValidationError) {
          this.store.values.email = ""
          this.errors.email = true
        }

        this.showFetchError = true
        this.showSpinner = false
      }
    }
  },

  // The edge case handling, when user's target weight is bigger than the current weight
  redirectIfWeightGain(url) {
    const isMetric = this.isMetric()

    if (isMetric) {
      if (this.store.values.targetWeightKg >= this.store.values.weightKg) {
        window.location = url
        return
      }
    } else {
      if (this.store.values.targetWeightLbs >= this.store.values.weightLbs) {
        window.location = url
        return
      }
    }

    // The component's content may be hidden before init for the better UI
    this.initContentHidden = false
  },

  // The edge case handling, when user's target weight is bigger than the current weight
  redirectToNext(isSkipStep) {
    if (isSkipStep) {
      window.location = this.nextStepPath
      return
    }

    // The component's content may be hidden before init for the better UI
    this.initContentHidden = false
  },

  goNext() {
    window.location = this.nextStepPath
  },
  
  logAnalyticsClickAndRedirect(clickName) {
    this.store.internalTracker.setEventToSessionStorage(clickName)
    window.location = this.nextStepPath
  },

  isLocalisedAssets() {
    const isEnabled = getFrontendCustomField({
      pipelineConfig,
      fieldName: 'useLocalisedAssets'
    })

    return isEnabled
  },

  isSplitMeasurementSystemLabels() {
    const isEnabled = getFrontendCustomField({
      pipelineConfig,
      fieldName: 'splitMeasurementSystemLabels'
    })

    return isEnabled
  }
})

export default commonView
